import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLazyQuery } from '@apollo/client';
import { Select } from '@guildeducationinc/recess';
import { Button } from '@mui/material';
import { DateRangeFilter } from '@src/components/DateRangeFilter';
import _ from 'lodash';
import { DatePicker, useLocale } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
import { GET_REPORT_COLUMNS } from '../../../graphql/queries/reportTemplate';
import { ReportType, } from '../../../types';
import { EmployeeReportSettings, EmployeeSpendReportSettings, ProgramSpendReportSettings, } from '../ReportTemplateSettings';
export const ReportConfig = ({ reportTemplate, onChange }) => {
    const { t } = useLocale();
    const [getReportColumns] = useLazyQuery(GET_REPORT_COLUMNS);
    const [reportColumns, setReportColumns] = useState([]);
    useEffect(() => {
        if (reportTemplate.type) {
            async function _getReportColumns() {
                const results = await getReportColumns({
                    variables: {
                        input: {
                            ..._.omit(reportTemplate, [
                                'id',
                                'createdAt',
                                'updatedAt',
                                '__typename',
                                'lastRunAt',
                                'lastRunStatus',
                                'lastRunMessage',
                            ]),
                            id: reportTemplate.id ? reportTemplate.id : null,
                            integrationConfigId: reportTemplate.integrationConfigId
                                ? reportTemplate.integrationConfigId
                                : null,
                            cronSchedules: reportTemplate.cronSchedules ?? null,
                            startDate: reportTemplate.startDate ?? null,
                            daysApart: reportTemplate.daysApart ? reportTemplate.daysApart : null,
                            publicKey: reportTemplate.publicKey ?? '',
                            extension: reportTemplate.extension ?? '',
                            shortName: reportTemplate.shortName ?? '',
                            filenamePattern: reportTemplate.filenamePattern ?? '',
                            type: reportTemplate.type,
                        },
                    },
                });
                if (results.data?.getReportColumns) {
                    setReportColumns(results.data.getReportColumns);
                    if (reportTemplate.settings.columns.length === 0) {
                        onChange({
                            ...reportTemplate,
                            settings: {
                                ...reportTemplate.settings,
                                columns: results.data.getReportColumns
                                    .filter((x) => x.default)
                                    .map((x) => x.code),
                            },
                        });
                    }
                }
            }
            _getReportColumns();
        }
    }, [reportTemplate.type]);
    const reportColumnOptions = useMemo(() => {
        const options = [];
        const lookup = {};
        reportColumns.map((column) => {
            if (!lookup[column.category]) {
                lookup[column.category] = [];
            }
            lookup[column.category].push(column);
        });
        Object.entries(lookup).forEach(([key, value]) => {
            options.push({
                label: t(key),
                options: value.map(col => ({
                    value: col.code,
                    label: col.label,
                })),
            });
        });
        return options;
    }, [reportColumns]);
    const handleColumnChange = (e) => {
        onChange({
            ...reportTemplate,
            settings: {
                ...reportTemplate.settings,
                columns: e.selection?.map((x) => x.value ?? []),
            },
        });
    };
    const handleResetDefaultColumns = () => {
        onChange({
            ...reportTemplate,
            settings: {
                spec: {
                    ...reportTemplate.settings.spec,
                },
                columns: reportColumns
                    .filter((x) => x.default)
                    .map((x) => x.code),
            },
        });
    };
    return (_jsxs("div", { className: 'flex flex-col gap-3', children: [_jsxs("div", { children: [_jsx(Select, { label: t('Columns'), options: reportColumnOptions, isSearchable: true, value: reportTemplate.settings.columns, onChange: handleColumnChange, multiple: true }), _jsx("div", { className: 'mt-1', children: _jsx(Button, { onClick: () => handleResetDefaultColumns(), variant: 'outlined', children: t('Reset to default columns') }) })] }), _jsx(DatePicker, { label: t('Start Date'), value: reportTemplate.settings.spec.startDate ?? '', onChange: dt => onChange({
                    ...reportTemplate,
                    settings: {
                        ...reportTemplate.settings,
                        spec: { ...reportTemplate.settings.spec, startDate: dt },
                    },
                }) }), _jsx(DatePicker, { label: t('End Date'), value: reportTemplate.settings.spec.endDate ?? '', onChange: dt => onChange({
                    ...reportTemplate,
                    settings: {
                        ...reportTemplate.settings,
                        spec: { ...reportTemplate.settings.spec, endDate: dt },
                    },
                }) }), _jsx(DateRangeFilter, { value: reportTemplate.settings.spec.dateRange?.toUpperCase(), onChange: dt => onChange({
                    ...reportTemplate,
                    settings: {
                        ...reportTemplate.settings,
                        spec: { ...reportTemplate.settings.spec, dateRange: dt },
                    },
                }) }), reportTemplate.type === ReportType.EMPLOYEE && (_jsx(EmployeeReportSettings, { settings: reportTemplate.settings, onChange: settings => onChange({ ...reportTemplate, settings }) })), reportTemplate.type === ReportType.EMPLOYEE_SPEND && (_jsx(EmployeeSpendReportSettings, { settings: reportTemplate.settings, onChange: settings => onChange({ ...reportTemplate, settings }) })), reportTemplate.type === ReportType.PROGRAM_SPEND && (_jsx(ProgramSpendReportSettings, { settings: reportTemplate.settings, onChange: settings => onChange({ ...reportTemplate, settings }) }))] }));
};
